
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Department.css'; // Your CSS file
import { usePortalMenuPosition, useOutsideClose } from '../../../../../hooks/usePortalDropdown';
import { AccessDenied } from '../../../../../shared-ui/States';

// --- Configuration ---
const AUTH_TOKEN_KEY = 'zTaskAuthToken';
const USER_DETAILS_KEY = 'zTaskUserDetails';
// Retrieve the base URL from the .env file for centralized configuration.
const API_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;


/**
 * A reusable dropdown component with search and clear functionality.
 */
const SearchableDropdown = ({
    options,
    value,
    onChange,
    placeholder,
    labelKey,
    valueKey,
    disabled,
    isLoading,
    renderOption,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const { menuStyle } = usePortalMenuPosition(isOpen, dropdownRef);
    useOutsideClose(isOpen, setIsOpen, dropdownRef, menuRef, () => setSearchTerm(''));

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        const lowercasedTerm = searchTerm.toLowerCase();
        return options.filter(option => {
            const displayLabel = renderOption ? renderOption(option) : option[labelKey] || '';
            return displayLabel.toLowerCase().includes(lowercasedTerm);
        });
    }, [options, searchTerm, labelKey, renderOption]);

    const selectedOption = useMemo(() => {
        return options.find(option => String(option[valueKey]) === String(value));
    }, [options, value, valueKey]);

    const handleSelect = (option) => {
        onChange(option ? option[valueKey] : ''); // Pass empty string on clear
        setIsOpen(false);
        setSearchTerm('');
    };

    const toggleDropdown = () => {
        if (!disabled && !isLoading) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className={`searchable-dropdown-container${isOpen ? ' is-open' : ''}`} ref={dropdownRef}>
            <div
                className={`dropdown-header ${disabled ? 'disabled' : ''} ${isLoading ? 'loading' : ''}${isOpen ? ' open' : ''}`}
                onClick={toggleDropdown}
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
            >
                {selectedOption ? (
                    <span className="selected-value">{renderOption ? renderOption(selectedOption) : selectedOption[labelKey]}</span>
                ) : (
                    <span className="placeholder">{placeholder}</span>
                )}
                <div className="dropdown-controls">
                    <span className="dropdown-arrow"></span>
                </div>
            </div>
            {isOpen && createPortal(
                <div className="dropdown-list-wrapper ui-portal-dropdown-menu" style={menuStyle} ref={menuRef}>
                    <div className="dropdown-search-container ui-portal-dropdown-search">
                        <input
                            type="text"
                            className="dropdown-search-input"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                        />
                         <span className="search-icon" />
                    </div>
                    <ul className="dropdown-list">
                        {!searchTerm && (
                             <li
                                className="dropdown-list-clear-item"
                                onClick={() => handleSelect(null)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSelect(null)}
                                tabIndex={0}
                            >
                                — Clear Selection —
                            </li>
                        )}
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <li key={option[valueKey]} onClick={() => handleSelect(option)} onKeyDown={(e) => e.key === 'Enter' && handleSelect(option)} tabIndex={0}>
                                    {renderOption ? renderOption(option) : option[labelKey]}
                                </li>
                            ))
                        ) : (
                            searchTerm && <li className="no-options">No results found</li>
                        )}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};


const Department = () => {
    // State for company details to check companyKeyword
    const [companyDetails, setCompanyDetails] = useState(null);

    // States for assigning department (Admin View)
    const [adminUsers, setAdminUsers] = useState([]);
    const [adminDepartments, setAdminDepartments] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
    const [isFetchingAdminUsers, setIsFetchingAdminUsers] = useState(false);
    const [fetchAdminUsersError, setFetchAdminUsersError] = useState('');
    const [isFetchingAdminDepartments, setIsFetchingAdminDepartments] = useState(false);
    const [fetchAdminDepartmentsError, setFetchAdminDepartmentsError] = useState('');
    const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
    const [assignmentSuccessMessage, setAssignmentSuccessMessage] = useState('');
    const [assignmentErrorMessage, setAssignmentErrorMessage] = useState('');

    // States for creating department MODAL (Admin View)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
    const [createModalSuccessMessage, setCreateModalSuccessMessage] = useState('');
    const [createModalErrorMessage, setCreateModalErrorMessage] = useState('');

    // States for User Read-Only View
    const [viewUsersWithDepartments, setViewUsersWithDepartments] = useState([]);
    const [isLoadingViewData, setIsLoadingViewData] = useState(false);
    const [viewDataError, setViewDataError] = useState('');

    // RBAC State
    const [currentUserRole, setCurrentUserRole] = useState(null);
    const [isLoadingRole, setIsLoadingRole] = useState(true);

    useEffect(() => {
        setIsLoadingRole(true);
        const storedUserDetailsString = localStorage.getItem(USER_DETAILS_KEY);
        let roleName = null;
        if (storedUserDetailsString) {
            try {
                const userDetails = JSON.parse(storedUserDetailsString);
                if (userDetails?.role?.name) {
                    roleName = userDetails.role.name.toUpperCase();
                }
            } catch (e) { console.error("DepartmentPage: Error parsing user details:", e); }
        }
        setCurrentUserRole(roleName);
        setIsLoadingRole(false);
    }, []);

    const fetchCompanyDetails = useCallback(async () => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) {
            console.error('Authentication token not found.');
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/api/get-company`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch company details: ${response.status}`);
            }
            const data = await response.json();
            if (data.message && data.message.length > 0) {
                setCompanyDetails(data.message[0]);
            } else {
                throw new Error("Company data not found in response.");
            }
        } catch (error) {
            console.error(error.message);
        }
    }, []);

    const fetchAdminUsers = useCallback(async () => {
        if (!['SUPER_ADMIN','ADMIN'].includes(currentUserRole)) return;
        setIsFetchingAdminUsers(true); setFetchAdminUsersError('');
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) { setFetchAdminUsersError("Auth token missing."); setIsFetchingAdminUsers(false); return; }
        try {
            // The constant API_BASE_URL is now constructed from the environment variable
            const response = await fetch(`${API_BASE_URL}/api/get-users`, { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
            if (!response.ok) { const txt = await response.text(); throw new Error(`Users API Error: ${response.status} - ${txt}`); }
            const data = await response.json();
            setAdminUsers(Array.isArray(data.userData) ? data.userData.map(u => ({...u, id: String(u.id)})) : []);
        } catch (error) { setFetchAdminUsersError(error.message); setAdminUsers([]); }
        finally { setIsFetchingAdminUsers(false); }
    }, [currentUserRole]);

    const fetchAdminDepartments = useCallback(async (selectLastCreated = false, justCreatedName = '') => {
        if (!['SUPER_ADMIN','ADMIN'].includes(currentUserRole)) return;
        setIsFetchingAdminDepartments(true); setFetchAdminDepartmentsError('');
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) { setFetchAdminDepartmentsError("Auth token missing."); setIsFetchingAdminDepartments(false); return; }
        try {
            // The constant API_BASE_URL is now constructed from the environment variable
            const response = await fetch(`${API_BASE_URL}/api/get-departments`, { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
            if (!response.ok) { const txt = await response.text(); throw new Error(`Depts API: ${response.status} - ${txt}`); }
            const data = await response.json();
            const fetchedDepts = Array.isArray(data.departments) ? data.departments.map(d => ({...d, departmentId: String(d.departmentId)})) : [];
            
            setAdminDepartments(fetchedDepts);

            if (selectLastCreated && justCreatedName && fetchedDepts.length > 0) {
                const justCreated = fetchedDepts.find(d => d.departmentName === justCreatedName);
                if (justCreated) {
                    setSelectedDepartmentId(justCreated.departmentId);
                } else if (fetchedDepts.length > 0) {
                    setSelectedDepartmentId(fetchedDepts[fetchedDepts.length - 1].departmentId);
                }
            } else if (!selectLastCreated) {
                setSelectedDepartmentId(prevSelectedId => {
                    if (prevSelectedId && !fetchedDepts.some(d => d.departmentId === prevSelectedId)) {
                        return '';
                    }
                    return prevSelectedId;
                });
            }
        } catch (error) { setFetchAdminDepartmentsError(error.message); setAdminDepartments([]); }
        finally { setIsFetchingAdminDepartments(false); }
    }, [currentUserRole]);


    const fetchUserDepartmentDataForView = useCallback(async () => {
        if (isLoadingRole || ['SUPER_ADMIN','ADMIN'].includes(currentUserRole) || !currentUserRole) return;
        setIsLoadingViewData(true); setViewDataError('');
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) { setViewDataError("Auth token missing."); setIsLoadingViewData(false); return; }
        try {
            // The constant API_BASE_URL is now constructed from the environment variable
            const response = await fetch(`${API_BASE_URL}/api/get-users`, { headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
            if (!response.ok) { const txt = await response.text(); throw new Error(`View Users API: ${response.status} - ${txt}`); }
            const data = await response.json(); setViewUsersWithDepartments(data.userData || []);
        } catch (err) { setViewDataError(err.message); setViewUsersWithDepartments([]); }
        finally { setIsLoadingViewData(false); }
    }, [currentUserRole, isLoadingRole]);

    useEffect(() => {
        if (isLoadingRole) return;
        if (['SUPER_ADMIN','ADMIN'].includes(currentUserRole)) {
            fetchCompanyDetails();
            fetchAdminUsers();
            fetchAdminDepartments();
        } else if (currentUserRole) {
            fetchUserDepartmentDataForView();
        }
    }, [currentUserRole, isLoadingRole, fetchAdminUsers, fetchAdminDepartments, fetchUserDepartmentDataForView, fetchCompanyDetails]);

    useEffect(() => { if (assignmentSuccessMessage) { const timer = setTimeout(() => setAssignmentSuccessMessage(''), 4000); return () => clearTimeout(timer); } }, [assignmentSuccessMessage]);
    useEffect(() => { if (assignmentErrorMessage) { const timer = setTimeout(() => setAssignmentErrorMessage(''), 5000); return () => clearTimeout(timer); } }, [assignmentErrorMessage]);

    const handleAssignSubmit = async (event) => {
        event.preventDefault();
        if (!selectedUserId) { setAssignmentErrorMessage('Please select a user.'); return; }
        if (!selectedDepartmentId) { setAssignmentErrorMessage('Please select a department.'); return; }
        setIsSubmittingAssignment(true); setAssignmentSuccessMessage(''); setAssignmentErrorMessage('');
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) { setAssignmentErrorMessage('Auth token not found.'); setIsSubmittingAssignment(false); return; }

        try {
            // The constant API_BASE_URL is now constructed from the environment variable
            const response = await fetch(`${API_BASE_URL}/api/assign-department`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json', }, body: JSON.stringify({ userId: selectedUserId, departmentId: selectedDepartmentId }) });
            const resText = await response.text(); let resData = { message: resText }; try { resData = JSON.parse(resText); } catch (e) {}
            if (!response.ok) throw new Error(resData.message || `API Error: ${response.status}`);
            setAssignmentSuccessMessage(resData.message || 'Department assigned successfully!');
            setSelectedUserId('');
            setSelectedDepartmentId('');
        } catch (error) { setAssignmentErrorMessage(`Assignment Failed: ${error.message}`); }
        finally { setIsSubmittingAssignment(false); }
    };

    const openCreateDepartmentModal = () => { setNewDepartmentName(''); setCreateModalSuccessMessage(''); setCreateModalErrorMessage(''); setIsCreateModalOpen(true); };
    const closeCreateDepartmentModal = () => setIsCreateModalOpen(false);

    const handleCreateDepartmentSubmit = async (event) => {
        event.preventDefault();
        const trimmedName = newDepartmentName.trim();
        if (!trimmedName) { setCreateModalErrorMessage('Department Name is required.'); return; }
        setIsSubmittingCreate(true); setCreateModalSuccessMessage(''); setCreateModalErrorMessage('');
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) { setCreateModalErrorMessage('Auth token not found.'); setIsSubmittingCreate(false); return; }
        const queryParams = new URLSearchParams({ departmentName: trimmedName });
        try {
            // The constant API_BASE_URL is now constructed from the environment variable
            const response = await fetch(`${API_BASE_URL}/api/add-department?${queryParams.toString()}`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
            const resText = await response.text(); let resData = { message: resText }; try { resData = JSON.parse(resText); } catch (e) {}
            if (!response.ok) throw new Error(resData.message || `Failed: ${response.status}`);
            setCreateModalSuccessMessage(resData.message || 'Department added successfully!');
            await fetchAdminDepartments(true, trimmedName);
            setNewDepartmentName('');
            setTimeout(() => closeCreateDepartmentModal(), 2500);
        } catch (error) { setCreateModalErrorMessage(`Creation Failed: ${error.message}`); }
        finally { setIsSubmittingCreate(false); }
    };

    const userPlaceholderText = isFetchingAdminUsers
        ? "Loading users..."
        : (adminUsers.length === 0 && !fetchAdminUsersError ? "No users available" : "-- Select a User --");

    const departmentPlaceholderText = isFetchingAdminDepartments
        ? "Loading departments..."
        : (adminDepartments.length === 0 && !fetchAdminDepartmentsError ? "No departments available" : "-- Select a Department --");

    if (isLoadingRole) return <div className="department-page-container loading-role-indicator">Verifying user access...</div>;

    if (['SUPER_ADMIN','ADMIN'].includes(currentUserRole)) {
        return (
            <div className="assign-department-page-container">
                <div className="main-header">
                    <div className="header-content">
                        <h2>Manage User Departments</h2>
                        <p>Assign users to departments, create new departments, and view existing ones.</p>
                    </div>
                    {/* Conditionally render the button based on companyKeyword */}
                    {companyDetails && companyDetails.companyKeyword !== 'PL' && (
                        <button type="button" onClick={openCreateDepartmentModal} className="create-new-department-button">
                            + Create New Department
                        </button>
                    )}
                </div>

                <form className="assign-department-form" onSubmit={handleAssignSubmit}>
                    <h3>Assign Department to User</h3>
                    {assignmentSuccessMessage && <div className="form-message success">{assignmentSuccessMessage}</div>}
                    {assignmentErrorMessage && <div className="form-message error">{assignmentErrorMessage}</div>}
                    <div className="form-group">
                        <label htmlFor="selectUser">Select User <span className="required-indicator">*</span></label>
                        {fetchAdminUsersError && <p className="data-fetch-error">{fetchAdminUsersError}</p>}
                        <SearchableDropdown
                            options={adminUsers}
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            placeholder={userPlaceholderText}
                            labelKey="name"
                            valueKey="id"
                            disabled={isFetchingAdminUsers || isSubmittingAssignment || adminUsers.length === 0}
                            isLoading={isFetchingAdminUsers}
                            renderOption={(user) => `${user.name} (${user.email})`}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="selectDepartmentAssign">Select Department <span className="required-indicator">*</span></label>
                         <SearchableDropdown
                            options={adminDepartments}
                            value={selectedDepartmentId}
                            onChange={setSelectedDepartmentId}
                            placeholder={departmentPlaceholderText}
                            labelKey="departmentName"
                            valueKey="departmentId"
                            disabled={isFetchingAdminDepartments || isSubmittingAssignment || adminDepartments.length === 0}
                            isLoading={isFetchingAdminDepartments}
                        />
                        {fetchAdminDepartmentsError && !isFetchingAdminDepartments && adminDepartments.length === 0 && (<p className="data-fetch-error">Could not load departments for selection: {fetchAdminDepartmentsError}</p>)}
                    </div>
                    <button type="submit" className="assign-department-button" disabled={isSubmittingAssignment || isFetchingAdminUsers || isFetchingAdminDepartments}>
                        {isSubmittingAssignment ? <><span className="spinner" /> Assigning...</> : 'Assign Department to User'}
                    </button>
                </form>

                <div className="manage-departments-section">
                    <h3>Existing Departments</h3>
                    {isFetchingAdminDepartments && <p className="info-message">Loading department list...</p>}
                    {fetchAdminDepartmentsError && !isFetchingAdminDepartments && (<p className="data-fetch-error">Error fetching departments: {fetchAdminDepartmentsError}</p>)}
                    {!isFetchingAdminDepartments && !fetchAdminDepartmentsError && adminDepartments.length === 0 && (<p className="info-message">No departments found. Create one.</p>)}
                    {!isFetchingAdminDepartments && !fetchAdminDepartmentsError && adminDepartments.length > 0 && (
                        <ul className="department-list">
                            {adminDepartments.map(dept => (
                                <li key={dept.departmentId} className="department-list-item">
                                    <span className="department-name">{dept.departmentName}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {isCreateModalOpen && ( <div className="modal-overlay"><div className="modal-content"><h2>Create New Department</h2><form onSubmit={handleCreateDepartmentSubmit}>{createModalSuccessMessage && <div className="form-message success modal-message">{createModalSuccessMessage}</div>}{createModalErrorMessage && <div className="form-message error modal-message">{createModalErrorMessage}</div>}<div className="form-group"><label htmlFor="newDepartmentNameModal">Department Name <span className="required-indicator">*</span></label><input type="text" id="newDepartmentNameModal" value={newDepartmentName} onChange={(e) => setNewDepartmentName(e.target.value)} placeholder="Enter department name" disabled={isSubmittingCreate} required autoFocus /></div><div className="modal-actions"><button type="button" onClick={closeCreateDepartmentModal} className="button-cancel" disabled={isSubmittingCreate}>Cancel</button><button type="submit" className="button-create add-department-button" disabled={isSubmittingCreate || !!createModalSuccessMessage}>{isSubmittingCreate ? <><span className="spinner" /> Creating...</> : 'Create Department'}</button></div></form></div></div>)}
            </div>
        );
    }
    else if (currentUserRole) { return ( <div className="user-departments-view-container department-page-container"><div className="main-header view-header"><div className="header-content"><h2>User Department Overview</h2><p>List of users and their assigned departments.</p></div></div>{isLoadingViewData && <div className="view-loading-container">Loading user department information...</div>}{viewDataError && <div className="view-error-container">Error: {viewDataError}</div>}{!isLoadingViewData && !viewDataError && (viewUsersWithDepartments.length === 0 ? (<p className="no-data-message">No users found or no users have assigned departments.</p>) : (<table className="user-departments-table"><thead><tr><th>User ID</th><th>Name</th><th>Email</th><th>Department</th></tr></thead><tbody>{viewUsersWithDepartments.map(user => (<tr key={user.id}><td>{user.id}</td><td>{user.name || 'N/A'}</td><td>{user.email}</td><td>{user.department ? (user.department.departmentName || user.department.name || 'Assigned - Name N/A') : (user.departmentName || 'Not Assigned')}</td></tr>))}</tbody></table>)) }</div>); }
    else {
        return (
            <div className="department-page-container">
                <AccessDenied description="Please log in with appropriate permissions." />
            </div>
        );
    }
};

export default Department;